/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import {
    SessionCreateDeviceTypeEnum,
    SessionCreateDrmTypeEnum,
    SessionCreateTransportEncodingTypeEnum,
} from 'client-code/session-manager';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { TSessionCreateModel } from '../interfaces';
import { ErrorTypes } from '../../error';
import { TCreateSessionParams } from '../../types/TCreateSessionParams';
import { DeviceModel } from '../../device';
import { TSession } from '../../types';
import { RetryMechanism } from '../../retrymechanism/RetryMechanism';
import { UniqueIdentifier } from '../../utils/UniqueIdentifier';
import { RetryOptions } from '../../retrymechanism/RetryOptions';
import { Log, VuLogger } from '../../logging';
import { StreamingSessionImpl } from './StreamingSessionImpl';

export class SocuStreamingSessionImpl extends StreamingSessionImpl {
    TAG: string = 'SocuStreamingSessionImpl';

    private static oISocuStreamingSession: SocuStreamingSessionImpl;

    static getInstance(): SocuStreamingSessionImpl {
        if (!SocuStreamingSessionImpl.oISocuStreamingSession)
            SocuStreamingSessionImpl.oISocuStreamingSession = new SocuStreamingSessionImpl();
        return SocuStreamingSessionImpl.oISocuStreamingSession;
    }

    createStreamingSession(sessionCreateModel: TSessionCreateModel): void {
        const { channel, transportEncodingType, watchAction } = sessionCreateModel;
        const { partnerStationId } = channel;
        const { partnerRootOfferId, partnerAssetId, partnerId } = watchAction?.kernel || {};

        if (!partnerId && !partnerRootOfferId && !partnerAssetId) {
            this.handlePlaybackError(ErrorTypes.LIVE_TV_DEVICE_ERROR_MISSING_CONFIG_V904);
            return;
        }
        // Need to update devicetype, drmtype, transportEncodingType based on actual data
        const sessionParams: TCreateSessionParams = {
            deviceType: SessionCreateDeviceTypeEnum.IptvSmartTv,
            drmType: SessionCreateDrmTypeEnum.Widevine,
            partnerId: partnerId || '',
            partnerServicesCustomerId: DeviceModel.getTiVoSettings().getPartnerCustomerId(),
            partnerStationId: partnerStationId || '',
            transportEncodingType: <SessionCreateTransportEncodingTypeEnum>transportEncodingType,
            partnerRootOfferId: partnerRootOfferId || '',
            partnerAssetId: partnerAssetId || '',
        };

        const createSession = this.services.createSession.bind(this.services);
        clearTimeout(this.timeoutId);
        clearInterval(this.timeoutId);
        // TODO: need to check if retry is needed.
        RetryMechanism.callServiceWithRetry<TSession>(
            createSession,
            [sessionParams, UniqueIdentifier.getNextSessionNetworkCallId()],
            <RetryOptions>this.SESSION_MANAGER_DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TSession) => {
                if (response.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                    this.oSession = response.session;
                    this.onSessionCreated.emit(this.oSession);
                    const { firstKeepAliveSeconds, keepAliveIntervalSeconds, sessionId } =
                        response.session;
                    VuLogger.setSessionId(sessionId);
                    this.timeoutId = setTimeout(() => {
                        // Time in second converting to millisecond
                        this.setKeepAliveInterval(keepAliveIntervalSeconds * 1000);
                        // Time in second converting to millisecond
                    }, firstKeepAliveSeconds && firstKeepAliveSeconds * 1000);
                }
            })
            .catch((error: any) => {
                // service is unreachable
                if (error.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                    this.handleSessionError(error);
                    Diagnostics.error(this.TAG, `{createStreamingSession: ${error}}`);
                    Log.e(this.TAG, `{createStreamingSession: ${error}}`);
                }
            });
    }
}
